import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Play, Square, RefreshCw, Terminal, CheckCircle2,
  XCircle, AlertTriangle, ChevronDown, ChevronUp,
  SkipForward, Mail, Database,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { streamMigration } from '@/services/api'

/* ── Status chips ───────────────────────────────────── */
const STATUS_CFG = {
  success:  { label: 'Migrated',  cls: 'chip-success' },
  failed:   { label: 'Failed',    cls: 'bg-destructive/10 text-destructive' },
  migrating:{ label: 'Migrating', cls: 'chip-amber', spin: true },
  pending:  { label: 'Pending',   cls: 'bg-black/6 text-foreground/45' },
}

function StatusChip({ status }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.pending
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border-0',
      cfg.cls
    )}>
      {cfg.spin && <RefreshCw className="w-2.5 h-2.5 animate-spin" />}
      {cfg.label}
    </span>
  )
}

/* ── Helpers ─────────────────────────────────────────── */
const fmt = (b) => {
  if (!b) return '0 B'
  const u = ['B','KiB','MiB','GiB'], i = Math.floor(Math.log(b) / Math.log(1024))
  return `${(b / 1024**i).toFixed(1)} ${u[i]}`
}

const LINE_CLR = {
  command: 'text-amber-700',
  stdout:  'text-emerald-700',
  stderr:  'text-red-600',
  error:   'text-red-700',
  success: 'text-emerald-700',
  warning: 'text-amber-600',
  info:    'text-sky-700',
  system:  'text-violet-700',
  default: 'text-foreground/55',
}

/* ── Component ───────────────────────────────────────── */
/* Map a persisted backend account status to a terminal chip status */
const mapStatus = (backendStatus) => {
  switch ((backendStatus || '').toUpperCase()) {
    case 'COMPLETED': return 'success'
    case 'FAILED':    return 'failed'
    case 'RUNNING':   return 'migrating'
    default:          return 'pending'
  }
}

export function MigrationTerminal({ jobId, accounts = [], initialProgress = [], onComplete, onCancel, className }) {
  const [isExecuting,     setIsExecuting]     = useState(false)
  const [isComplete,      setIsComplete]      = useState(false)
  const [showTerminal,    setShowTerminal]    = useState(false)
  const [lines,           setLines]           = useState([])
  const [progress,        setProgress]        = useState({
    current:0, total:accounts.length, success:0, failed:0, pending:accounts.length
  })
  const [accountStatuses, setAccountStatuses] = useState({})
  const [selected,        setSelected]        = useState(() => new Set())
  const [resyncMaxAge,    setResyncMaxAge]    = useState(0)   // 0 = full sync
  const [error,           setError]           = useState(null)

  const streamRef    = useRef(null)
  const outputEndRef = useRef(null)

  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior:'smooth' })
  }, [lines])

  // Seed account statuses from persisted progress so revisiting a migrated job
  // shows previous results + percentages (and enables resync).
  useEffect(() => {
    const byEmail = {}
    initialProgress.forEach(p => { if (p?.email) byEmail[p.email] = p })
    const s = {}
    let success = 0, failed = 0, pending = 0
    accounts.forEach(a => {
      const p = byEmail[a.email]
      const status = p ? mapStatus(p.status) : 'pending'
      if (status === 'success') success++
      else if (status === 'failed') failed++
      else pending++
      s[a.email] = {
        status,
        output: [],
        messagesMigrated:     p?.emailsMigrated || 0,
        totalSourceMessages:  p?.totalEmails || 0,
        sourceSize:           p?.sourceMailboxSize || 0,
        destSize:             p?.destinationMailboxSize || 0,
        sourceMessages:       p?.sourceMessages || 0,
        destMessages:         p?.destinationMessages || 0,
        currentFolder:        p?.currentFolder || '',
        progressPercent:      p?.progressPercentage || (status === 'success' ? 100 : 0),
        message:              status === 'failed' ? (p?.errorMessage || 'Failed') : undefined,
      }
    })
    setAccountStatuses(s)
    setProgress({ current: success + failed, total: accounts.length, success, failed, pending })
    // If a prior run left completed/failed accounts and none are running, treat
    // as a finished run so the resync/retry controls are shown.
    if ((success + failed) > 0 && pending === 0) setIsComplete(true)
  }, [accounts, initialProgress])

  const addLine = (type, content, meta = {}) =>
    setLines(prev => [...prev, {
      type,
      content: typeof content === 'string' ? content : JSON.stringify(content),
      timestamp: new Date().toLocaleTimeString(),
      id: `${Date.now()}-${Math.random()}`,
      ...meta,
    }])

  const handleStreamMessage = useCallback((type, data) => {
    switch (type) {
      case 'init':
        addLine('info', `Starting migration — ${accounts.length} accounts`)
        break
      case 'progress':
        setProgress(p => ({ ...p, current:data.current, total:data.total }))
        addLine('command', `[${data.current}/${data.total}] ${data.email}`, { email:data.email })
        setAccountStatuses(p => ({ ...p, [data.email]: { ...p[data.email], status:'migrating' } }))
        break
      case 'account-progress':
        setAccountStatuses(p => ({
          ...p,
          [data.email]: {
            ...p[data.email],
            status:'migrating',
            messagesMigrated:data.messagesMigrated,
            totalSourceMessages:data.totalSourceMessages,
            sourceMessages:data.sourceMessages,
            destMessages:data.destMessages,
            sourceSize:data.sourceSize,
            destSize:data.destSize,
            currentFolder:data.currentFolder,
            folderTotal:data.folderTotal,
            folderMigrated:data.folderMigrated,
            progressPercent:data.progressPercent,
          },
        }))
        break
      case 'command': addLine('info', data); break
      case 'output':  addLine('stdout', `  ${data.line}`, { email:data.email }); break
      case 'error': {
        const m = typeof data==='string' ? data : (data.line||data.message||JSON.stringify(data))
        addLine('stderr', `  ${m}`, { email:data?.email })
        break
      }
      case 'success':
        addLine('success', `  Migrated: ${data.email}`, { email:data.email })
        setProgress(p => ({ ...p, success:p.success+1, pending:p.pending-1 }))
        setAccountStatuses(p => ({
          ...p,
          [data.email]: {
            ...p[data.email], status:'success', message:data.message,
            sourceSize:data.sourceSize||p[data.email]?.sourceSize||0,
            destSize:data.destSize||p[data.email]?.destSize||0,
            sourceMessages:data.sourceMessages||p[data.email]?.sourceMessages||0,
            destMessages:data.destMessages||p[data.email]?.destMessages||0,
            messagesMigrated:data.messagesMigrated||p[data.email]?.messagesMigrated||0,
          },
        }))
        break
      case 'failed':
        addLine('error', `  Failed: ${data.email}`, { email:data.email })
        setProgress(p => ({ ...p, failed:p.failed+1, pending:p.pending-1 }))
        setAccountStatuses(p => ({
          ...p, [data.email]: { ...p[data.email], status:'failed', message:data.message }
        }))
        break
      case 'complete': {
        const { failed=0, success=0, total=0 } = data
        if (failed===total)  addLine('error',   `Complete — all ${total} failed.`)
        else if (failed>0)   addLine('warning',  `Complete — ${success} migrated, ${failed} failed.`)
        else                 addLine('success',  `Complete — all ${success} migrated.`)
        setIsExecuting(false); setIsComplete(true); onComplete?.(data)
        break
      }
      default: addLine('default', data)
    }
  }, [accounts.length, onComplete])

  const startMig = useCallback(async (options = {}) => {
    if (!jobId || isExecuting) return
    setIsExecuting(true); setIsComplete(false); setError(null); setLines([])
    setProgress(p => ({ ...p, current:0, success:0, failed:0 }))
    if (Array.isArray(options.emails)) {
      const ageNote = options.maxAge ? ` · last ${options.maxAge} days` : ''
      addLine('system', `Resyncing ${options.emails.length} account(s)${ageNote}…`)
      setAccountStatuses(p => {
        const n = { ...p }
        options.emails.forEach(e => { n[e] = { ...n[e], status:'pending', message:undefined } })
        return n
      })
    } else if (options.resume)     addLine('system', 'Resuming — skipping completed accounts…')
    else if (options.failedOnly)   addLine('system', 'Retrying failed accounts…')
    else                           addLine('system', 'Initialising migration stream…')

    try {
      streamRef.current = streamMigration(jobId, {
        onMessage: ({ type, data }) => handleStreamMessage(type, data),
        onError: (err) => {
          const m = err?.message||err?.type||'Connection error'
          setError(m); addLine('error', 'Connection failed: '+m); setIsExecuting(false)
        },
        onComplete: () => setIsExecuting(false),
      }, options)
    } catch (err) {
      const m = err?.message||String(err)
      setError(m); addLine('error', 'Failed to start: '+m); setIsExecuting(false)
    }
  }, [jobId, isExecuting, handleStreamMessage])

  const retryFailed   = useCallback(() => {
    const failed = Object.entries(accountStatuses)
      .filter(([,s]) => s?.status==='failed').map(([e]) => e)
    if (failed.length) startMig({ emails:failed })
  }, [accountStatuses, startMig])

  const resumeMig     = useCallback(() => startMig({ resume:true }), [startMig])
  const cancelExec    = useCallback(() => {
    streamRef.current?.close()
    addLine('warning', 'Migration cancelled')
    setIsExecuting(false); onCancel?.()
  }, [onCancel])

  // ── Resync ─────────────────────────────────────────────
  // Re-runs imapsync for the chosen accounts. imapsync is idempotent — it skips
  // mail already on the destination and only copies what's new, so this is a
  // safe "catch up on new emails" without re-migrating everything.
  const resyncableEmails = accounts
    .filter(a => accountStatuses[a.email]?.status === 'success')
    .map(a => a.email)

  const toggleSelect = (email) => setSelected(prev => {
    const n = new Set(prev)
    n.has(email) ? n.delete(email) : n.add(email)
    return n
  })

  const toggleSelectAll = () => setSelected(prev =>
    (prev.size === resyncableEmails.length && resyncableEmails.length > 0)
      ? new Set()
      : new Set(resyncableEmails)
  )

  const resyncSelected = useCallback(() => {
    const emails = [...selected]
    if (!emails.length) return
    setSelected(new Set())
    startMig({ emails, maxAge: resyncMaxAge })
  }, [selected, resyncMaxAge, startMig])

  const resyncOne = useCallback((email) => {
    setSelected(new Set())
    startMig({ emails:[email], maxAge: resyncMaxAge })
  }, [resyncMaxAge, startMig])

  const failedCount  = progress.failed
  const successCount = progress.success
  const pct = progress.total > 0
    ? Math.round(((successCount + failedCount) / progress.total) * 100) : 0

  const variant = isComplete
    ? failedCount === 0 ? 'success' : successCount === 0 ? 'error' : 'partial'
    : null

  return (
    <div className={cn('space-y-3', className)}>

      {/* ── Control panel ── */}
      <div className="soft-card rounded-2xl overflow-hidden !transform-none">

        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/40">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-primary/60" />
            <span className="text-sm font-medium text-foreground/80">Email Migration</span>
            {accounts.length > 0 && (
              <span className="text-xs text-muted-foreground">· {accounts.length} accounts</span>
            )}
          </div>

          {/* Live badge */}
          {isExecuting && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium chip-amber px-2.5 py-0.5 rounded-full">
              <RefreshCw className="w-2.5 h-2.5 animate-spin" /> Running
            </span>
          )}
          {variant === 'success' && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium chip-success px-2.5 py-0.5 rounded-full">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </span>
          )}
          {variant === 'partial' && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium chip-warning px-2.5 py-0.5 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Partial
            </span>
          )}
          {variant === 'error' && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-destructive/10 text-destructive px-2.5 py-0.5 rounded-full">
              <XCircle className="w-3 h-3" /> Failed
            </span>
          )}
        </div>

        <div className="px-5 py-4 space-y-3.5">
          {/* Progress bar */}
          {(isExecuting || isComplete) && progress.total > 0 && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{pct}% complete</span>
                <span>{successCount + failedCount} / {progress.total}</span>
              </div>
              <div className="h-1.5 bg-black/8 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-700',
                    variant === 'success' ? 'bg-success' :
                    variant === 'error'   ? 'bg-destructive' : 'bg-primary'
                  )}
                  style={{ width:`${pct}%` }}
                />
              </div>
              <div className="flex gap-4 text-[11px]">
                {successCount > 0 && <span className="text-success">{successCount} migrated</span>}
                {failedCount  > 0 && <span className="text-destructive">{failedCount} failed</span>}
                {!isComplete  && progress.total - successCount - failedCount > 0 && (
                  <span className="text-muted-foreground">
                    {progress.total - successCount - failedCount} pending
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Completion summary banner */}
          {isComplete && (
            <div className={cn(
              'rounded-xl px-4 py-3 text-sm border-0',
              variant === 'success' ? 'bg-success/10 text-success' :
              variant === 'partial' ? 'bg-warning/10 text-warning' :
              'bg-destructive/10 text-destructive'
            )}>
              {variant === 'success' && `All ${successCount} accounts migrated.`}
              {variant === 'partial' && `${successCount} migrated, ${failedCount} failed — retry below.`}
              {variant === 'error'   && `All ${failedCount} accounts failed — retry below.`}
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="border-0 bg-destructive/8 text-destructive rounded-xl py-2">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}

          {/* Action row */}
          <div className="flex gap-2 flex-wrap">
            {!isExecuting && !isComplete && (
              <Button size="sm" onClick={() => startMig()} disabled={!jobId || accounts.length === 0}>
                <Play className="w-3.5 h-3.5 mr-1.5" />
                Start Migration
              </Button>
            )}

            {isExecuting && (
              <Button size="sm" variant="destructive" onClick={cancelExec}>
                <Square className="w-3.5 h-3.5 mr-1.5" />
                Cancel
              </Button>
            )}

            {isComplete && (
              <>
                <Button size="sm" variant="outline" onClick={resumeMig}>
                  <SkipForward className="w-3.5 h-3.5 mr-1.5" />
                  Resume
                </Button>
                {failedCount > 0 && (
                  <Button size="sm" onClick={retryFailed}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    Retry Failed ({failedCount})
                  </Button>
                )}
                <Button size="sm" variant="ghost"
                  onClick={() => { setIsComplete(false); setLines([]); setAccountStatuses({}) }}
                >
                  Reset
                </Button>
              </>
            )}

            {/* Resync — pick accounts below, then re-pull new mail for them */}
            {!isExecuting && resyncableEmails.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={resyncMaxAge}
                  onChange={(e) => setResyncMaxAge(Number(e.target.value))}
                  className="h-8 rounded-md border border-black/10 bg-white/70 px-2 text-xs text-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/40"
                  title="How far back to scan on resync"
                >
                  <option value={0}>All mail</option>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
                <Button
                  size="sm"
                  variant={selected.size > 0 ? 'default' : 'outline'}
                  onClick={resyncSelected}
                  disabled={selected.size === 0}
                  title={selected.size === 0 ? 'Select migrated accounts below to resync' : 'Re-pull new mail for the selected accounts'}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Resync Selected ({selected.size})
                </Button>
              </div>
            )}

            {/* Log toggle — pushed right */}
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-muted-foreground hover:text-foreground/70"
              onClick={() => setShowTerminal(v => !v)}
            >
              <Terminal className="w-3.5 h-3.5 mr-1.5" />
              {showTerminal ? 'Hide' : 'Show'} Log
              {showTerminal ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Terminal log pane — warm dark surface ── */}
      {showTerminal && (
        <div className="rounded-2xl overflow-hidden border border-stone-800/80"
          style={{ background: 'rgba(28, 22, 16, 0.92)', backdropFilter:'blur(16px)' }}
        >
          {/* Pane header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8">
            <div className="flex items-center gap-2.5">
              {/* macOS-style dots */}
              <span className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60"  />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              </span>
              <span className="text-[11px] font-mono text-stone-400 ml-1">imapsync output</span>
              {isExecuting && <RefreshCw className="w-2.5 h-2.5 animate-spin text-amber-400/70" />}
            </div>
            {lines.length > 0 && (
              <button
                className="text-[11px] text-stone-500 hover:text-stone-300 transition-colors"
                onClick={() => setLines([])}
              >
                Clear
              </button>
            )}
          </div>

          {/* Log output */}
          <div className="h-64 overflow-y-auto px-4 py-3 font-mono text-[11px] leading-relaxed space-y-px">
            {lines.length === 0 ? (
              <div className="text-stone-600">
                <p>$ # imapsync terminal</p>
                <p>$ # Press "Start Migration" to begin</p>
              </div>
            ) : (
              lines.map(line => (
                <div key={line.id} className={cn('break-all', LINE_CLR[line.type] ?? LINE_CLR.default)}>
                  {line.type === 'command' && (
                    <span className="text-stone-600 mr-1.5">{line.timestamp}</span>
                  )}
                  {line.content}
                </div>
              ))
            )}
            {isExecuting && <div className="text-amber-500/70 animate-pulse">▌</div>}
            <div ref={outputEndRef} />
          </div>
        </div>
      )}

      {/* ── Account status rows ── */}
      {accounts.length > 0 && (
        <div className="soft-card rounded-2xl overflow-hidden !transform-none">
          <div className="px-5 py-2.5 border-b border-white/40 bg-black/[0.02] flex items-center justify-between">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Account Status
            </span>
            {!isExecuting && resyncableEmails.length > 0 && (
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-[11px] font-medium text-primary/70 hover:text-primary transition-colors"
              >
                {selected.size === resyncableEmails.length ? 'Clear selection' : `Select all migrated (${resyncableEmails.length})`}
              </button>
            )}
          </div>

          <div className="divide-y divide-white/30 max-h-80 overflow-y-auto">
            {accounts.map((account) => {
              const st         = accountStatuses[account.email]
              const isMig      = st?.status === 'migrating'
              const isFailed   = st?.status === 'failed'
              const isDone     = st?.status === 'success'
              const pctAcc     = Math.min(st?.progressPercent || 0, 100)

              return (
                <div key={account.email} className="px-5 py-3 space-y-1.5 hover:bg-white/20 transition-colors">
                  {/* Header row */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {isDone && !isExecuting ? (
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 shrink-0 accent-primary cursor-pointer"
                          checked={selected.has(account.email)}
                          onChange={() => toggleSelect(account.email)}
                          title="Select for resync"
                        />
                      ) : (
                        <Mail className="w-3.5 h-3.5 text-primary/40 shrink-0" />
                      )}
                      <span className="text-[12px] font-mono text-foreground/75 truncate">{account.email}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusChip status={st?.status || 'pending'} />
                      {isFailed && !isExecuting && (
                        <Button size="sm" variant="outline"
                          className="h-5 px-2 text-[10px]"
                          onClick={() => startMig({ emails:[account.email] })}
                        >
                          <RefreshCw className="w-2.5 h-2.5 mr-1" />
                          Retry
                        </Button>
                      )}
                      {isDone && !isExecuting && (
                        <Button size="sm" variant="ghost"
                          className="h-5 px-2 text-[10px] text-primary/70 hover:text-primary"
                          onClick={() => resyncOne(account.email)}
                          title="Re-pull new mail for this account"
                        >
                          <RefreshCw className="w-2.5 h-2.5 mr-1" />
                          Resync
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* In-progress details */}
                  {isMig && (
                    <div className="pl-5 space-y-1">
                      <div className="flex justify-between text-[11px] text-muted-foreground">
                        <span className="truncate max-w-[60%]">
                          {st?.currentFolder || 'Scanning…'}
                        </span>
                        <span>{pctAcc}%</span>
                      </div>
                      <div className="h-1 bg-black/8 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width:`${pctAcc}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[11px] text-muted-foreground">
                        <span>Folder {st?.folderMigrated||0}/{st?.folderTotal||'?'}</span>
                        <span>{st?.messagesMigrated||0}/{st?.totalSourceMessages||'?'} msgs</span>
                      </div>
                    </div>
                  )}

                  {/* Messages + size row (source vs destination) */}
                  {(st?.sourceSize > 0 || st?.destSize > 0 || st?.sourceMessages > 0 || st?.destMessages > 0) && (
                    <div className="flex gap-4 pl-5 text-[11px] text-muted-foreground">
                      <span>Source: <span className="text-foreground/70 font-mono">{(st?.sourceMessages || 0).toLocaleString()} msgs · {fmt(st.sourceSize)}</span></span>
                      <span>Dest: <span className="text-foreground/70 font-mono">{(st?.destMessages || 0).toLocaleString()} msgs · {fmt(st.destSize)}</span></span>
                    </div>
                  )}

                  {/* Status message */}
                  {st?.message && (
                    <p className="pl-5 text-[11px] text-muted-foreground truncate">{st.message}</p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default MigrationTerminal
